/**
 * A walkable scene in the shell (integration plan slice 6a, ADR 0031): the
 * walking model (`@ab/scene`), the scene view (`@ab/renderer` `mountScene`)
 * and the loop that drives both, the input, the routes, the portals and the
 * sprites. Composition only: where the traveler may go, how fast and where a
 * route leads are `@ab/scene`'s rules; how it looks is the renderer's; what a
 * hatching job does is the server's (ADR 0034; `@ab/session`'s reducer draws it).
 *
 * - **Loop:** a `requestAnimationFrame` loop steps the model by the frame's
 *   time (at most `uiTiming.sceneMaxStepMs`) and hands it to the view, but a
 *   frame is asked for only while something needs one: a held key or pad, a
 *   route, a walker moving (`@ab/scene` `pending`), the view's own wants
 *   (Medium's continuous systems, the camera settling, a time of day blending,
 *   a hatch playing; `update`'s answer). Otherwise the loop stops: a
 *   patroller's wait is a timer that wakes it when the resident sets off (the
 *   time in between passes with `rest`), a sprite's idle loop at Off steps
 *   from a timer without a frame request, and input, a resize, a tier change,
 *   a landed sheet or texture and the page coming back wake it. A hidden page
 *   stops it.
 * - **Motion:** the system's `prefers-reduced-motion` sets the view's motion
 *   strength (0: no flicker, breeze, wind, water motion, points or birds).
 * - **Input:** the keys and the pad set a held direction (screen up is north;
 *   several keys add up), Shift runs. Any key or pad press cancels a route.
 *   A tap picks the ground under it (`pickGround`) and routes there by A*
 *   (`routeTo`); a second tap re-routes; a swipe does nothing here.
 * - **Portals:** entering one fades the veil in (`uiTiming.sceneFadeMs`),
 *   swaps the scene record for the portal's target at its spawn, holds
 *   (`sceneFadeHoldMs`) and fades out; input is ignored meanwhile. The
 *   airship's gangplank (`kind: disembark`) is such a portal: the ship is only
 *   walkable while moored, so it always leads to the town it is moored at.
 * - **Sprites:** the sheets of `virtual:ab-assets` load into the view after
 *   mount (Brad's `idle`, `walk` and `run`, and a hatchling's species); the
 *   residents have no export yet and draw as the renderer's proxies (the
 *   airship's crew as the prototype's cards).
 * - **Interactions (slice 6b):** the interactable in reach (`@ab/scene`
 *   `interactableNear`) is the snapshot's `prompt` (a `ui.prompt.<kind>`
 *   key); `action` (E, Space, the pad's centre, a tap on the prompt) uses it:
 *   a `talk` opens a dialogue with the resident's name and `<human>.talk`, a
 *   `read` one titled `scene.<id>.read_<interactable>` with its pages, a
 *   `wish` the one line `ui.wish.made` (once per visit: `spent`), a `door`
 *   crosses as walking through its portal does, a `shop` opens the market of
 *   the scene's place (read-only rows until the wallet, ADR 0032), and a
 *   `board` (the town's mooring) crosses to the airship through its portal
 *   (ADR 0033). On the airship: the wheel (`fly`) opens the course menu (the
 *   routes from the town the ship is moored at, `rules/routes.yaml`; no cost
 *   until roadmap step 6); a route opens the packing panel (slice 8f, ADR
 *   0041: the bag's stacks and storage's, Auto, Empty, Go, Cancel; `pack.ts`),
 *   and Go sets the ship flying (`setFlight`), holds
 *   the banner `ui.banner.flying_to` for `uiTiming.flightMs`, then hands the
 *   destination and the bag to the shell (`onFly`), which runs the dungeon and brings the
 *   traveler back to the wheel's spawn (its `target`). An `incubator` (the
 *   hatchery's) places an egg aboard (`ui.prompt.place`; a menu of the eggs
 *   when there are several species), says the time still needed while it
 *   incubates (`ui.prompt.wait`: `ui.hatch.remaining`), and hatches a ready
 *   one (`ui.prompt.hatch`): the server's claim (ADR 0034; placing and
 *   claiming are requests, the prompt hidden until the answer, and a refusal
 *   or no answer a one-line dialogue `ui.server.<code>`; the job's state and
 *   time left are drawn at the server's time), the view's hatch sequence (it
 *   holds the walker, as a dialogue does, and ends when the view's sequence
 *   says so: its event clock, never a timer here), then the banner
 *   `ui.banner.hatched`, and the hatchling stands at the hatch spot (a guest,
 *   drawn on every entry until the traveler leaves the ship). With more than
 *   one egg aboard the incubator asks which (a menu of the species aboard and
 *   their counts).
 *   While a dialogue or a menu is open (or the shell's pause menu:
 *   `setPaused`), or a hatch plays, the model is `held`: the traveler stands
 *   and every patroller waits, through a crossing too (a crossing waits for a
 *   hatch to end). The snapshot's `arrival` counts arrivals: the location's name shows
 *   on each, and an arrival may bring a banner (the egg aboard).
 * - **Time of day (ADR 0032, 0033):** the scene shows the lighting preset the
 *   page asked for its kind of scene (`time.ts`: the town's and the ship's are
 *   kept apart) when it has it, else its default; the pause menu cycles the
 *   scene's presets (`lightings`), `setLighting` switches the view without a
 *   rebuild, and a crossing keeps each kind's choice.
 * - **The ship (ADR 0033):** a ship scene is its record with what its rooms
 *   hold (`resolveShipScene`: the room types' workstations, spawns and
 *   passages placed by the grid); the crew stand at the posts of the time of
 *   day, moored or flying (`applyCrew`), again whenever either changes.
 */
import { type BagEntry, type Dir, dirOffset } from '@ab/game-core';
import { type EggState, type GroundPoint, type QualityTier, type Scene, type SceneView, mountScene } from '@ab/renderer';
import { type Offer, type SceneState, applyCrew, cancelRoute, createSceneState, createWalkMap, interactableNear, marketOffers, pending, portalFor, resolveShipScene, rest, routeTo, sceneRooms, shipWalkExtras, step } from '@ab/scene';
import type { ShownError, SkipPlan, StorageStack, TicketCounts } from '@ab/api-contract';
import { type Locale, type RosterMonster, type Strings, type TextArgs, type TimedJob, jobRemaining, jobState } from '@ab/session';
import { type BannerRequest, uiTiming } from '@ab/ui';
import { textures } from 'virtual:ab-assets';
import { pack, routes, scenes, shipRooms, strings as sourceTables, towns } from 'virtual:ab-content';
import { type SpritesReport, spriteLoader, spriteSources, tables } from './shared.ts';
import type { AccountStore } from './account.ts';
import { adjust, afterRefusal, auto, confirmBagRow, fitted, flown, moveIn, startingBag } from './pack.ts';
import { type TimeChoice, type TimeKind, timeKindOf } from './time.ts';

/** The traveler (the default party's leader, Brad): the sheet the scene view draws it with. */
const travelerSpecies = 'human.brad';

/** An egg choice: a species aboard and how many of it. */
export type EggChoice = { readonly species: string; readonly count: number };

/**
 * What is open over the scene: a dialogue (string keys: the speaker's, or null for a line nobody says, and the pages;
 * `args` fills a page's placeholders), a shop's menu (the keeper's name key and what the market sells, read-only:
 * ADR 0032), the airship's course (the wheel: destinations by id) or the eggs to place in an incubator (ADR 0033).
 */
export type SceneOverlay =
  | { readonly kind: 'dialogue'; readonly speaker: string | null; readonly pages: readonly string[]; readonly page: number; readonly args?: TextArgs }
  | { readonly kind: 'shop'; readonly keeper: string; readonly offers: readonly Offer[] }
  | { readonly kind: 'course'; readonly destinations: readonly string[] }
  /**
   * The packing panel (slice 8f, ADR 0041; Q85): the bag being packed for `destination` and the storage it is packed
   * from (as last read; read again as the panel opens), `line` a refusal it says (`ui.pack.full`, `ui.pack.refused`),
   * `failed` the storage read's (its line and Try again). Go flies with the bag; Cancel returns to the wheel.
   */
  | {
      readonly kind: 'pack';
      readonly destination: string;
      readonly bag: readonly BagEntry[];
      readonly storage: readonly StorageStack[];
      readonly line: string | null;
      readonly failed: ShownError | null;
      /** The account is being read again (the storage shown is the cached one meanwhile; Go waits for it). */
      readonly reading: boolean;
      /** The server's refusals since the player opened the panel (`pack.ts` `afterRefusal`). */
      readonly refusals: number;
      /** Refused three times: Go is dimmed until the player opens the panel again. */
      readonly stuck: boolean;
    }
  | { readonly kind: 'eggs'; readonly slot: string; readonly eggs: readonly EggChoice[] }
  /**
   * An incubating egg's menu (slice 8c, ADR 0039; tiers since slice 8e, ADR 0040, and shown with no tickets too): the
   * time left, the tickets by tier, Skip with the server's plan (`skipPrice` at the server's time of the read just
   * made; dimmed, with "Not enough tickets.", when the wallet cannot cover it) and Cancel. Skip pays nothing yet: it
   * opens the confirmation (`skip`, slice 8h).
   */
  | {
      readonly kind: 'incubating';
      readonly slot: string;
      readonly job: string;
      readonly time: TimeText;
      readonly price: SkipPlan;
      readonly tickets: TicketCounts;
      /** The plans to choose by hand the read answered (`skipOptions`), for the confirmation. */
      readonly options: readonly SkipPlan[];
    }
  /**
   * The skip's confirmation (slice 8h, ADR 0042; Q88): the game's plan first (`auto`), then each tier the wallet holds
   * whose tickets alone cover the wait (`tier:<id>`, the server's `skipOptions`), then Cancel; the time left, and for
   * the choice under the cursor the minutes it wastes beyond the time left (none: no line). Confirming a choice spends
   * exactly its tickets (the game's row sends `auto`: the server plans it at its own time). `line`: the server refused
   * the last choice (`ui.server.not_enough_tickets`, `ui.server.bad_plan`); the account was read again and this is the
   * menu anew. Since the fix round it is never stale: the account is read as it opens (`reading`: the rows of the last
   * read, dimmed, confirm nothing until it lands), the time left and the waste are worked out again every
   * `uiTiming.skipTickMs` and on every read, and a read that fails (`failed`) shows its line and Try again
   * (`retry`) instead of any row; a job that became ready meanwhile closes it (its prompt says Hatch).
   */
  | {
      readonly kind: 'skip';
      readonly slot: string;
      readonly job: string;
      readonly left: TimeText;
      readonly tickets: TicketCounts;
      readonly choices: readonly SkipChoice[];
      readonly line: string | null;
      readonly reading: boolean;
      readonly failed: ShownError | null;
    };

/** A duration as the text shows it: `ui.hatch.hours`, `ui.hatch.hours_only` or `ui.hatch.minutes` and their numbers. */
export type TimeText = { readonly key: string; readonly values: Readonly<Record<string, number>> };

/** A row of the skip's confirmation: its plan, and what it wastes beyond the time left (null: nothing). */
export type SkipChoice = { readonly key: string; readonly plan: SkipPlan; readonly waste: TimeText | null; readonly auto: boolean };

export type SceneSnapshot = {
  readonly sceneId: string;
  /** The key of the place's name on arrival (`<scene>.name`; a ship's room by its type, `ui.room.<type>`). */
  readonly nameKey: string;
  readonly locale: Locale;
  readonly strings: Strings;
  readonly tier: QualityTier;
  /** The ground point a tap-to-route walk heads for; null when none runs. */
  readonly route: GroundPoint | null;
  /** The veil is up (a portal crossing). */
  readonly fading: boolean;
  /** The prompt's word key (`ui.prompt.<kind>`) for the interactable in reach; null when none (or while something is open). */
  readonly prompt: string | null;
  readonly overlay: SceneOverlay | null;
  /** Arrivals so far (1 on the first view, one more per crossing): the location's name shows on each. */
  readonly arrival: number;
  /** The time of day shown (a preset of the scene), and every preset of the scene in its record's order (ADR 0033). */
  readonly lighting: string;
  readonly lightings: readonly string[];
  /** The destination the airship flies to while its flight's banner holds (ADR 0033); null otherwise. */
  readonly flying: string | null;
  /** A banner over the scene (an egg aboard, a hatchling); null when none shows. */
  readonly banner: BannerRequest | null;
  /** A hatch sequence is playing (no prompt meanwhile). */
  readonly hatching: boolean;
};

export type SceneController = {
  readonly view: SceneView;
  readonly snapshot: () => SceneSnapshot;
  readonly subscribe: (listener: () => void) => () => void;
  /** The walking model now (tests read it). */
  state(): SceneState;
  /** A held direction from a source (`keys`, `pad`): screen x right and y up; null releases it. Cancels a route. */
  hold(source: 'keys' | 'pad', dir: { readonly x: number; readonly y: number } | null): void;
  /** The pad's held 8-way direction. */
  holdDir(dir: Dir | null): void;
  setRun(run: boolean): void;
  /** Routes to the ground under a point of the canvas (CSS pixels); false when there is no ground or no route. */
  tap(x: number, y: number): boolean;
  cancelRoute(): void;
  /** Crosses to a scene's spawn as a portal does (tests: a scene swap without walking there). */
  travel(to: string, spawn: string): void;
  /** Animation frames asked for since mount (tests: Off idles with none). */
  frameRequests(): number;
  setLocale(locale: Locale): void;
  setTier(tier: QualityTier): void;
  resize(width: number, height: number): void;
  resizes(): number;
  /** Portal crossings since mount. */
  crossings(): number;
  sprites(): SpritesReport;
  /** Sets the model's traveler at a point (tests: a capture view), snapping the camera. */
  place(x: number, y: number): void;
  /** Turns the traveler where it stands (tests). */
  face(facing: SceneState['player']['facing']): void;
  /** Disposes the view and mounts a new one at `tier`; returns what the old one left on the GPU. */
  remount(tier: QualityTier): { readonly geometries: number; readonly textures: number };
  /** Uses the interactable in reach (slice 6b); false when there is none (or something is open). */
  action(): boolean;
  /** The open dialogue's next page, or its end (what it returns to opens then). */
  advance(): void;
  /** Closes what is open: a shop, a menu or a dialogue. */
  close(): void;
  /** Opens a dialogue of string keys (tests: every page of every locale measured at 390 px). */
  say(speaker: string | null, pages: readonly string[]): void;
  /** A shop menu's row confirmed: `leave` closes it (the goods are read-only rows until the wallet, ADR 0032). */
  shop(row: string): void;
  /** The course menu's row confirmed: a destination opens the packing panel for it (slice 8f); `cancel` closes it (ADR 0033). */
  course(row: string): void;
  /**
   * The packing panel's row confirmed (slice 8f, ADR 0041): `store:<item>` packs a full stack (or tops the bag's up;
   * "The bag is full." when a new stack has no slot), `bag:<item>` takes the stack out when its count is at "×0" (0)
   * and does nothing otherwise, `auto`, `empty`, `retry` (reads storage again), `go` flies with the bag (after the
   * storage read in progress, if any), `cancel` closes it.
   */
  pack(row: string): void;
  /** A bag row's count one step down or up (left, right, a tap on − or +), down to "×0". */
  packAdjust(row: string, step: -1 | 1): void;
  /**
   * The server refused the bag (`bad_bag`): the panel opens again for `destination` with that bag cut to storage as
   * read again (or Auto's choice, or Go dimmed: `pack.ts` `afterRefusal`) and its line.
   */
  repack(destination: string, bag: readonly BagEntry[]): void;
  /** The egg menu's row confirmed: an egg species is placed; `cancel` closes it. */
  egg(row: string): void;
  /** The incubating egg's menu row confirmed: `skip` opens the skip's confirmation (slice 8h); `cancel` closes it. */
  incubating(row: string): void;
  /**
   * The skip's confirmation row confirmed (slice 8h, ADR 0042): `auto` (the server's choice at its time) or
   * `tier:<id>` (exactly that row's tickets) spends; `retry` reads the account again after a failed read; `cancel`
   * closes it, nothing spent. Nothing but Cancel acts while the account is being read.
   */
  skipChoice(row: string): void;
  /** Shows a banner over the scene (the shell's: a mail's claim, slice 8c). */
  banner(key: string, values: Readonly<Record<string, string | number>>): void;
  /** The time of day now (a preset of the scene; ADR 0032, 0033): the view switches without a rebuild; later crossings keep it. */
  setLighting(lighting: string): void;
  /** The shell's pause menu is open: the model is held as for a dialogue. */
  setPaused(paused: boolean): void;
  /** Re-reads the ship's jobs (the eggs' states, the prompt) at the server's time now (after the tests moved its clock and the account was read again). */
  refreshShip(): void;
  /** Moors the ship again after a flight the shell could not finish (the run's view could not be built). */
  land(): void;
  /** Stops the loop and frees the view (the dungeon takes the canvas); returns what the view left on the GPU. */
  dispose(): { readonly geometries: number; readonly textures: number };
};

/**
 * What the shell is told (ADR 0033): the wheel flew to `destination` with the bag the packing panel chose (slice 8f);
 * the shell runs it and brings the traveler back to `spawn` of `from` (the deck at the wheel).
 */
export type SceneHandlers = { readonly onFly?: (destination: string, spawn: string, from: string, bag: readonly BagEntry[]) => void };

/** What a scene starts with besides its record: the account (the ship's eggs, jobs and roster), and a banner to show on arrival (an egg aboard). */
export type SceneStart = { readonly ship: AccountStore; readonly banner?: { readonly key: string; readonly names?: Readonly<Record<string, string>> } };

/** The key of a read interactable's title and its pages (`…_1`, `…_2`, … as far as the source table has them). */
const readKeys = (sceneId: string, interactable: string): { readonly title: string; readonly pages: readonly string[] } => {
  const title = `${sceneId}.read_${interactable}`;
  const pages: string[] = [];
  for (let n = 1; sourceTables.en[`${title}_${n}`] !== undefined; n += 1) pages.push(`${title}_${n}`);
  return { title, pages };
};

/** The scene holding a ship scene's room grid. */
const gridOf = (scene: Scene): Scene | undefined => (scene.onShip === undefined ? undefined : scenes[scene.onShip.ship]);

/** Each scene as the shell walks it: a ship scene with what its rooms hold (ADR 0033), once. */
const resolved = new Map<string, Scene>();
const sceneOf = (id: string): Scene => {
  const known = resolved.get(id);
  if (known !== undefined) return known;
  const scene = scenes[id];
  if (scene === undefined) throw new Error(`no scene ${id} in the content pack`);
  const r = resolveShipScene(scene, gridOf(scene), shipRooms);
  resolved.set(id, r);
  return r;
};

/** The spawn at a ship scene's wheel (its `fly` interactable's target): where a run resumed at boot comes back to (ADR 0034). */
export const wheelSpawn = (sceneId: string): string | undefined => sceneOf(sceneId).interactables.find((i) => i.kind === 'fly')?.target;

const isShip = (scene: Scene): boolean => scene.kind === 'ship_deck' || scene.kind === 'ship_room';

/**
 * The walking model of a scene at a spawn: a ship scene's rooms add their floors, walls and furnishings, and its crew
 * stand at the posts of the time of day `preset`, moored or `flying` (ADR 0033).
 */
const modelOf = (scene: Scene, spawn: string | undefined, preset: string, flying: boolean): SceneState => {
  const grid = gridOf(scene);
  if (!isShip(scene) || grid === undefined) return createSceneState(scene, spawn);
  const state = createSceneState(scene, spawn, createWalkMap(scene, shipWalkExtras(scene, sceneRooms(scene, grid, shipRooms))));
  applyCrew(state, preset, flying);
  return state;
};

/** A place's name on arrival: a ship's room by its type's label, any other scene by its record's name. */
const nameKeyOf = (scene: Scene): string => {
  const grid = gridOf(scene);
  const roomId = scene.kind === 'ship_room' ? scene.onShip?.rooms?.[0] : undefined;
  const type = roomId === undefined ? undefined : grid?.ship?.rooms.find((r) => r.id === roomId)?.type;
  return type === undefined ? `${scene.id}.name` : `ui.room.${type}`;
};

/** A time of day for a scene: the one asked for if the scene has it, else its default. */
const lightingFor = (scene: Scene, asked: string | null): string => (asked !== null && Object.hasOwn(scene.lighting.presets, asked) ? asked : scene.lighting.default);

/** Seconds left as a nested key: hours and minutes, or minutes (rounded up: a job a few seconds from ready still says 1m). */
const remainingKey = (seconds: number): TimeText => minutesKey(Math.max(1, Math.ceil(seconds / 60)));

/** Whole minutes as a nested key: hours and minutes from an hour on (whole hours alone, "2 hr"), else minutes. */
const minutesKey = (minutes: number): TimeText =>
  minutes < 60
    ? { key: 'ui.hatch.minutes', values: { minutes } }
    : minutes % 60 === 0
      ? { key: 'ui.hatch.hours_only', values: { hours: minutes / 60 } }
      : { key: 'ui.hatch.hours', values: { hours: Math.floor(minutes / 60), minutes: minutes % 60 } };

/**
 * The skip's confirmation's rows (slice 8h, ADR 0042; Q88): the server's plan first, then its single-tier options, each
 * with the minutes it pays beyond `left` (the whole minutes left, rounded up as the server counts them). The plans are
 * the server's (`skipPrice`, `skipOptions`, from the last read); only the waste is worked out here, for its line, from
 * the time left now (so it grows while the menu stays open; the fix round).
 */
const skipChoices = (price: SkipPlan, options: readonly SkipPlan[], left: number): SkipChoice[] => {
  const waste = (p: SkipPlan): TimeText | null => (p.minutes > left ? minutesKey(p.minutes - left) : null);
  return [
    { key: 'auto', plan: price, waste: waste(price), auto: true },
    ...options.map((p) => ({ key: `tier:${Object.keys(p.tickets).join('+')}`, plan: p, waste: waste(p), auto: false })),
  ];
};

export const createSceneController = (
  canvas: HTMLCanvasElement,
  sceneId: string,
  spawn: string | undefined,
  tier: QualityTier,
  locale: Locale,
  lighting: TimeChoice,
  handlers: SceneHandlers,
  start: SceneStart,
): SceneController => {
  const ship = start.ship;
  let scene = sceneOf(sceneId);
  /** The time of day the page asked for, per kind of scene (kept across crossings; a scene without it shows its default). */
  const lightingAsked: Record<TimeKind, string | null> = { ...lighting };
  const askedFor = (s: Scene): string | null => lightingAsked[timeKindOf(s.kind)];
  let state = modelOf(scene, spawn, lightingFor(scene, askedFor(scene)), false);
  const spritesOf = spriteLoader();
  /** The species the scene may draw: Brad, the residents, and the roster's (a hatchling at its hatch spot). */
  const species = (): readonly string[] => [travelerSpecies, ...scene.residents.map((r) => r.human), ...ship.state().roster.map((m) => m.species)];
  const show = (v: SceneView): void => {
    v.resize(canvas.clientWidth, canvas.clientHeight);
    // An interior draws with its town's textures (the town scene of the same place holds them); a ship scene with its own.
    const town = isShip(scene) ? undefined : Object.values(scenes).find((s) => s.place === scene.place && s.kind === 'town');
    const grid = gridOf(scene);
    v.show(scene, {
      traveler: travelerSpecies,
      model: state,
      lighting: lightingFor(scene, askedFor(scene)),
      textures: textures[scene.id] ?? (town === undefined ? undefined : textures[town.id]) ?? {},
      ...(spawn === undefined ? {} : { spawn }),
      ...(grid === undefined ? {} : { ship: { rooms: shipRooms, grid } }),
    });
  };
  // Set below: wakes the loop when the view wants a frame by itself.
  let onChange = (): void => {
    // Replaced by `wake` once the loop exists.
  };
  const mount = (t: QualityTier): SceneView =>
    mountScene(canvas, {
      tier: t,
      onChange: () => {
        onChange();
      },
    });
  let view = mount(tier);
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const applyMotion = (): void => {
    view.setMotion(motion.matches ? 0 : 1);
  };
  applyMotion();
  spritesOf.load(view, spriteSources.filter((s) => species().includes(s.id)));
  show(view);
  const listeners = new Set<() => void>();
  let bannerId = 1;
  const bannerOf = (key: string, names?: Readonly<Record<string, string>>): BannerRequest => ({ id: bannerId++, key, tone: 'plain', durationMs: uiTiming.banner, ...(names === undefined ? {} : { names }) });
  let snap: SceneSnapshot = {
    sceneId: scene.id,
    nameKey: nameKeyOf(scene),
    locale,
    strings: tables[locale],
    tier,
    route: null,
    fading: false,
    prompt: null,
    overlay: null,
    arrival: 1,
    lighting: lightingFor(scene, askedFor(scene)),
    lightings: Object.keys(scene.lighting.presets),
    flying: null,
    banner: start.banner === undefined ? null : bannerOf(start.banner.key, start.banner.names),
    hatching: false,
  };
  let paused = false;
  let disposed = false;
  /** A request to the server is out (placing an egg, a claim): no prompt, no action until it answers. */
  let asking = false;
  const notify = (): void => {
    for (const l of listeners) l();
  };
  /** Shows a banner for its time (one at a time: a new one replaces it). */
  const showBanner = (banner: BannerRequest): void => {
    snap = { ...snap, banner };
    notify();
    window.setTimeout(() => {
      if (disposed || snap.banner?.id !== banner.id) return;
      snap = { ...snap, banner: null };
      notify();
    }, banner.durationMs);
  };
  if (snap.banner !== null) showBanner(snap.banner);
  /**
   * "You have mail" once a session (slice 8c), when unread mail is known on arriving in a scene (or as soon as the
   * account's read brings it); after a banner already showing, never over a crossing, a flight or a hatch.
   */
  const mailBanner = (): void => {
    if (disposed || snap.fading || snap.flying !== null || snap.hatching || !ship.takeMailBanner()) return;
    const wait = snap.banner === null ? 0 : snap.banner.durationMs;
    window.setTimeout(() => {
      if (!disposed) showBanner(bannerOf('ui.banner.mail'));
    }, wait);
  };

  // ---- The airship's incubators (ADR 0033): jobs in the stand-in store, their state by the shell's clock.
  /** The job running in an incubator, if any. */
  const jobIn = (slot: string): TimedJob | null => ship.state().jobs.find((j) => j.slot === slot) ?? null;
  /** Each incubator's egg as the view shows it: this scene's (empty where nothing hatches) and the neighbour room's it draws too. */
  const eggStates = (): Record<string, EggState> => {
    const out: Record<string, EggState> = {};
    const now = ship.time.now();
    for (const i of scene.interactables) if (i.kind === 'incubator') out[i.id] = 'empty';
    for (const job of ship.state().jobs) out[job.slot] = jobState(job, now);
    return out;
  };
  /** The hatchlings standing in this scene (the store's guests, at their hatch spots). */
  const applyGuests = (): void => {
    view.setGuests(
      ship.guests().flatMap((g) => {
        const spot = g.scene === scene.id ? scene.interactables.find((i) => i.id === g.spot) : undefined;
        return spot === undefined ? [] : [{ id: g.id, species: g.species, x: spot.x, y: spot.y, facing: 's' as const }];
      }),
    );
  };
  let readyTimer: number | null = null;
  /** Shows the eggs, and wakes when the next one becomes ready (its prompt changes then). */
  const applyEggs = (): void => {
    view.setEggs(eggStates());
    if (readyTimer !== null) window.clearTimeout(readyTimer);
    readyTimer = null;
    const now = ship.time.now();
    const waits: number[] = [];
    for (const job of ship.state().jobs) if (jobState(job, now) === 'incubating') waits.push(jobRemaining(job, now));
    if (waits.length > 0) {
      // A timer's longest wait is about 24.8 days; a longer job is re-read when the timer fires.
      readyTimer = window.setTimeout(
        () => {
          readyTimer = null;
          if (disposed) return;
          applyEggs();
          refreshPrompt();
          // A skip's confirmation open on the egg that just became ready closes (the fix round).
          syncSkip();
        },
        Math.min(Math.min(...waits) * 1000 + 50, 2 ** 31 - 1),
      );
    }
  };
  applyEggs();
  applyGuests();

  const promptKind = { talk: 'ui.prompt.talk', read: 'ui.prompt.read', wish: 'ui.prompt.wish', door: 'ui.prompt.enter', shop: 'ui.prompt.shop', board: 'ui.prompt.board', fly: 'ui.prompt.fly' } as const;
  /** The prompt of an interactable: a word, or none (an empty incubator with no egg aboard). */
  const promptOf = (near: SceneState['scene']['interactables'][number]): string | null => {
    switch (near.kind) {
      case 'talk':
      case 'read':
      case 'wish':
      case 'door':
      case 'shop':
      case 'board':
      case 'fly':
        return promptKind[near.kind];
      case 'incubator': {
        const job = jobIn(near.id);
        if (job !== null) return jobState(job, ship.time.now()) === 'ready' ? 'ui.prompt.hatch' : 'ui.prompt.wait';
        return ship.state().eggs.length > 0 ? 'ui.prompt.place' : null;
      }
      case 'hatch_spot':
      case 'plot':
      case 'bench':
      case 'rack':
      case 'fixture':
        return null;
    }
  };
  /** The prompt for what is in reach now (none while something is open, paused, crossing, flying or hatching); published only when it changes. */
  const refreshPrompt = (): void => {
    const near = snap.overlay !== null || paused || asking || snap.fading || snap.flying !== null || snap.hatching ? null : interactableNear(state);
    const prompt = near === null ? null : promptOf(near);
    if (prompt === snap.prompt) return;
    snap = { ...snap, prompt };
    notify();
  };
  const unsubscribeShip = ship.subscribe(() => {
    syncPack();
    syncSkip();
    applyEggs();
    applyGuests();
    refreshPrompt();
    mailBanner();
  });
  /** Holds the model while anything is open or the pause menu is, the ship flies or a hatch plays (a new model after a crossing too). */
  const applyHeld = (): void => {
    state.held = snap.overlay !== null || paused || snap.flying !== null || snap.hatching;
  };
  /** The crew to the posts of the time of day shown, moored or under way (ADR 0033). */
  const placeCrew = (): void => {
    applyCrew(state, snap.lighting, snap.flying !== null);
  };
  /** Opens (or closes, with null) what shows over the scene; the model is held while anything is open. */
  const setOverlay = (overlay: SceneOverlay | null): void => {
    snap = { ...snap, overlay };
    applyHeld();
    if (state.held) {
      cancelRoute(state);
      snap = { ...snap, route: null };
    }
    notify();
    refreshPrompt();
    wake();
  };
  const held: Record<'keys' | 'pad', { x: number; y: number } | null> = { keys: null, pad: null };
  let run = false;
  let resizeCount = 0;
  let crossings = 0;
  let last: number | null = null;
  let frame: number | null = null;
  let timer: number | null = null;
  /** When the loop stopped (performance.now()), so the time it slept passes for the patrollers; null while running. */
  let sleptAt: number | null = null;
  let frames = 0;
  const hold = (source: 'keys' | 'pad', dir: { readonly x: number; readonly y: number } | null): void => {
    held[source] = dir === null || (dir.x === 0 && dir.y === 0) ? null : { x: dir.x, y: dir.y };
    if (held[source] !== null) {
      cancelRoute(state);
      setRoute(null);
    }
    wake();
  };
  const setRoute = (route: GroundPoint | null): void => {
    if (snap.route === route) return;
    snap = { ...snap, route };
    notify();
  };
  const cross = (to: string, at: string): void => {
    // A hatch plays to its end first (the walker is held meanwhile, so only a test's travel gets here).
    if (snap.hatching) return;
    snap = { ...snap, fading: true };
    notify();
    held.keys = null;
    held.pad = null;
    refreshPrompt();
    window.setTimeout(() => {
      if (disposed) return;
      // Down the gangplank to a town: the hatchlings standing aboard go with the traveler (the roster keeps them).
      const next = sceneOf(to);
      if (isShip(scene) && !isShip(next)) ship.clearGuests();
      scene = next;
      spawn = at;
      state = modelOf(scene, at, lightingFor(scene, askedFor(scene)), snap.flying !== null);
      // A menu open across the crossing keeps holding the new scene's residents and traveler.
      applyHeld();
      crossings += 1;
      view.setGuests([]);
      show(view);
      applyEggs();
      applyGuests();
      spritesOf.load(view, spriteSources.filter((s) => species().includes(s.id)));
      snap = { ...snap, sceneId: scene.id, nameKey: nameKeyOf(scene), route: null, arrival: snap.arrival + 1, lighting: lightingFor(scene, askedFor(scene)), lightings: Object.keys(scene.lighting.presets) };
      window.setTimeout(() => {
        if (disposed) return;
        snap = { ...snap, fading: false };
        notify();
        refreshPrompt();
        mailBanner();
        // Arriving (the town, boarding, a room): the mail is read again, so staff mail sent meanwhile shows (and its
        // banner, once a session).
        void ship.loadMail();
        wake();
      }, uiTiming.sceneFadeHoldMs);
      notify();
      wake();
    }, uiTiming.sceneFadeMs);
  };
  /**
   * The airship casts off (ADR 0033): the ship flies (sails set, the jetty astern, streaks) and the banner names the
   * destination while input waits; then the shell takes over (`onFly`). The traveler comes back to `back` (the wheel's
   * spawn) of this scene.
   */
  const fly = (destination: string, back: string, bag: readonly BagEntry[]): void => {
    snap = { ...snap, flying: destination };
    applyHeld();
    held.keys = null;
    held.pad = null;
    cancelRoute(state);
    setRoute(null);
    view.setFlight(true);
    placeCrew();
    refreshPrompt();
    notify();
    wake();
    window.setTimeout(() => {
      if (disposed) return;
      handlers.onFly?.(destination, back, scene.id, bag);
    }, uiTiming.flightMs);
  };
  /*
   * The packing panel (slice 8f, ADR 0041): opens over the wheel with the cached storage and a starting bag (the
   * remembered one where storage allows, else Auto's; or, after the server refused one, what `afterRefusal` says),
   * then reads the account again: the storage it brings re-makes the bag (a bag the player has not touched yet starts
   * again from the new storage); Go waits for that read; no answer is the panel's line with Try again.
   */
  /**
   * Where the open panel's bag came from, so a storage read re-makes it the same way: `start` (the remembered bag or
   * Auto's), `auto` (Auto's, after a second refusal), `refused` (the refused bag, cut to storage), `touched` (the
   * player changed it). Every source but `touched` is a bag the panel proposed itself.
   */
  let packSource: 'start' | 'auto' | 'refused' | 'touched' = 'start';
  /** The bag the last Go flew with was the panel's own proposal (`afterRefusal`'s `proposed`). */
  let goProposed = false;
  /** The refusals of the panel the last Go left (a refusal of that Go is one more). */
  let packRefusals = 0;
  /** Go was confirmed while storage was being read: it flies once the read is in (with the bag it re-fits). */
  let goWhenRead = false;
  const readPack = (): void => {
    if (ship.status() !== 'signed_in') return;
    const o = snap.overlay;
    if (o?.kind === 'pack') setOverlay({ ...o, reading: true, failed: null });
    void ship.refresh().then((error) => {
      const now = snap.overlay;
      if (disposed || now === null || now.kind !== 'pack') return;
      const failed = error === null || error === 'unauthorized' ? null : error;
      setOverlay({ ...now, reading: false, failed });
      const go = goWhenRead;
      goWhenRead = false;
      // Go waited for the read: it flies now with the bag re-fitted to the storage read; a failed read stays (its line).
      if (go && error === null) goPack();
    });
  };
  const openPack = (destination: string, refused?: { readonly bag: readonly BagEntry[]; readonly refusals: number }): void => {
    const storage = ship.state().storage;
    goWhenRead = false;
    const after = refused === undefined ? null : afterRefusal(refused.refusals, goProposed);
    packSource = after === null ? 'start' : after.bag === 'auto' ? 'auto' : 'refused';
    const bag = refused === undefined ? startingBag(ship.id(), storage) : after?.bag === 'auto' ? auto(storage) : fitted(refused.bag, storage);
    setOverlay({ kind: 'pack', destination, bag, storage, line: after?.line ?? null, failed: null, reading: false, refusals: refused?.refusals ?? 0, stuck: after?.stuck ?? false });
    readPack();
  };
  /** The account read again while the panel is open: the bag re-made from the storage it brought, as it was made. */
  const syncPack = (): void => {
    const o = snap.overlay;
    if (o === null || o.kind !== 'pack') return;
    const storage = ship.state().storage;
    if (storage === o.storage) return;
    const bag = packSource === 'start' ? startingBag(ship.id(), storage) : packSource === 'auto' ? auto(storage) : fitted(o.bag, storage);
    setOverlay({ ...o, storage, bag });
  };
  /** The panel's bag changed by the player; `line` replaces what it said (a stuck panel keeps its line). */
  const setPack = (bag: readonly BagEntry[], line: string | null = null): void => {
    const o = snap.overlay;
    if (o === null || o.kind !== 'pack') return;
    packSource = 'touched';
    setOverlay({ ...o, bag, line: o.stuck ? o.line : line });
  };
  /** Go: the ship flies with the stacks that have a count; a stuck panel's Go does nothing. */
  const goPack = (): void => {
    const o = snap.overlay;
    if (o === null || o.kind !== 'pack' || o.stuck) return;
    goProposed = packSource !== 'touched';
    packRefusals = o.refusals;
    setOverlay(null);
    const wheel = scene.interactables.find((i) => i.kind === 'fly');
    fly(o.destination, wheel?.target ?? scene.spawns[0]?.id ?? '', flown(o.bag));
  };
  /**
   * A line the server's refusal (or no answer) says, as a dialogue nobody speaks; the player may try again. Not for
   * `unauthorized`: the account signed out and the login is the one window then (`account.ts`).
   */
  const refusal = (error: ShownError): void => {
    if (error === 'unauthorized') return;
    setOverlay({ kind: 'dialogue', speaker: null, pages: [`ui.server.${error}`], page: 0 });
  };
  /** Runs a request to the server with the prompt hidden and actions refused until it answers (no double placing or claim). */
  const ask = async <T,>(work: () => Promise<T>): Promise<T> => {
    asking = true;
    refreshPrompt();
    try {
      return await work();
    } finally {
      asking = false;
      if (!disposed) refreshPrompt();
    }
  };
  /** Places an egg of `species` in an incubator: the server's answer (the ship shows it once the account is read again). */
  /**
   * An incubating egg (slice 8c; slice 8e, ADR 0040 D1): the ship is read again (the plan at the server's time now) and
   * the menu opens: the time left, the tickets held, Skip with the server's plan (dimmed, "Not enough tickets.", when
   * the wallet cannot cover it), Cancel. Without an account (a guest), its time left as a line.
   */
  const incubatingEgg = (slot: string, job: TimedJob): void => {
    const remaining = (j: TimedJob) => ({ kind: 'dialogue' as const, speaker: null, pages: ['ui.hatch.remaining'], page: 0, args: { keys: { time: remainingKey(jobRemaining(j, ship.time.now())) } } });
    if (ship.status() !== 'signed_in') {
      setOverlay(remaining(job));
      return;
    }
    void ask(() => ship.refresh()).then((error) => {
      if (disposed) return;
      if (error !== null) {
        refusal(error);
        return;
      }
      const now = jobIn(slot);
      if (now === null || jobState(now, ship.time.now()) === 'ready') return;
      const price = ship.state().skipPrices[now.id] ?? null;
      if (price === null) {
        setOverlay(remaining(now));
        return;
      }
      // The menu even with no tickets (slice 8e): the plan shows what a skip would take, dimmed when the wallet cannot.
      const time = remainingKey(jobRemaining(now, ship.time.now()));
      setOverlay({ kind: 'incubating', slot, job: now.id, time, price, tickets: ship.state().tickets, options: ship.state().skipOptions[now.id] ?? [] });
    });
  };
  /**
   * The skip's confirmation (slice 8h, ADR 0042; Q88) for the job in `slot`, from the account as last read: the game's
   * plan, each tier alone that covers the wait, Cancel; the time left and each choice's waste at the server's time now.
   * `line`: the refusal that brought it back. Nothing to skip (the job ready or gone): nothing opens.
   */
  const skipConfirm = (slot: string, line: string | null = null, reading = false): boolean => {
    const now = jobIn(slot);
    if (now === null || jobState(now, ship.time.now()) === 'ready') return false;
    const price = ship.state().skipPrices[now.id] ?? null;
    if (price === null) return false;
    const seconds = jobRemaining(now, ship.time.now());
    const choices = skipChoices(price, ship.state().skipOptions[now.id] ?? [], Math.max(1, Math.ceil(seconds / 60)));
    setOverlay({ kind: 'skip', slot, job: now.id, left: remainingKey(seconds), tickets: ship.state().tickets, choices, line, reading, failed: null });
    return true;
  };
  /** The whole minutes left at the confirmation's last read (a tick reads again once they change). */
  let skipReadLeft: number | null = null;
  /** The whole minutes the job in `slot` has left at the server's time now, as the server counts them; null: no job. */
  const minutesLeftIn = (slot: string): number | null => {
    const job = jobIn(slot);
    return job === null ? null : Math.max(1, Math.ceil(jobRemaining(job, ship.time.now()) / 60));
  };
  let skipTimer: number | null = null;
  const stopSkipTick = (): void => {
    if (skipTimer !== null) window.clearInterval(skipTimer);
    skipTimer = null;
  };
  /**
   * The open confirmation made again from the account as last read and the server's time now (the fix round): the time
   * left and every row's waste, the rows the last read's. A job that became ready (or went) meanwhile closes it: the
   * incubator's prompt says Hatch. While a read is out or after one failed, it is left as it is.
   */
  const syncSkip = (): void => {
    const o = snap.overlay;
    if (o === null || o.kind !== 'skip' || o.reading || o.failed !== null) return;
    if (!skipConfirm(o.slot, o.line)) setOverlay(null);
  };
  /**
   * Reads the account for the open confirmation. `shown`: the rows wait (dimmed, confirming nothing) until it lands, and
   * a failure replaces them with its line and Try again; otherwise (a tick's read) a failure keeps the rows, worked out
   * from the server's clock. A read that lands makes the menu again from it.
   */
  const readSkip = (shown: boolean): void => {
    const o = snap.overlay;
    if (o === null || o.kind !== 'skip') return;
    if (shown) setOverlay({ ...o, reading: true });
    void ship.refresh().then((error) => {
      const now = snap.overlay;
      if (disposed || now === null || now.kind !== 'skip' || now.job !== o.job) return;
      // Signed out: the login is the one window now.
      if (error === 'unauthorized') return;
      if (error !== null) {
        if (now.reading) setOverlay({ ...now, reading: false, failed: error });
        return;
      }
      skipReadLeft = minutesLeftIn(now.slot);
      setOverlay({ ...now, reading: false, failed: null });
      syncSkip();
    });
  };
  /** Every `uiTiming.skipTickMs` while the confirmation is open: worked out again, and read again once a minute passed. */
  const startSkipTick = (): void => {
    stopSkipTick();
    skipTimer = window.setInterval(() => {
      const o = snap.overlay;
      if (disposed || o === null || o.kind !== 'skip') {
        stopSkipTick();
        return;
      }
      syncSkip();
      if (!o.reading && o.failed === null && minutesLeftIn(o.slot) !== skipReadLeft) readSkip(false);
    }, uiTiming.skipTickMs);
  };
  /**
   * Opens the confirmation (the fix round: never stale): at once with the rows of the last read, dimmed, while the
   * account is read again; the rows the read brings replace them. `line`: the refusal that brought it back. When the
   * account's last read cannot make it (`failed`: the refused choice's re-read failed), it opens on that line with
   * Try again, no rows.
   */
  const openSkip = (slot: string, line: string | null, failed: ShownError | null = null): boolean => {
    const job = jobIn(slot);
    if (job === null) return false;
    if (failed !== null) {
      setOverlay({ kind: 'skip', slot, job: job.id, left: remainingKey(jobRemaining(job, ship.time.now())), tickets: ship.state().tickets, choices: [], line, reading: false, failed });
    } else if (!skipConfirm(slot, line, true)) {
      return false;
    }
    startSkipTick();
    if (failed === null) readSkip(true);
    return true;
  };
  /**
   * Spends the tickets of the choice the player confirmed (the game's row: `auto`, planned by the server at its own
   * time): the server's answer (the egg is ready once the account is read again). Refused for its tickets
   * (`not_enough_tickets`, or a plan the server will not take, `bad_plan`; nothing spent): the confirmation opens anew
   * with the refusal's line, from the account read again; when that read failed, on its line with Try again, never
   * with the rows from before. Any other refusal is its line.
   */
  const skipEgg = (slot: string, job: string, plan: TicketCounts | 'auto'): void => {
    void ask(() => ship.skip(job, plan)).then(({ refused, read }) => {
      if (disposed) return;
      if (refused === null) {
        if (read !== null) refusal(read);
        return;
      }
      const again = (refused === 'not_enough_tickets' || refused === 'bad_plan') && read !== 'unauthorized' && jobIn(slot)?.id === job;
      if (!again) {
        refusal(refused);
        return;
      }
      const line = `ui.server.${refused}`;
      if (read !== null) {
        openSkip(slot, line, read);
        return;
      }
      // Opened from the read just made. The job became ready (or went) meanwhile: it stays closed, and the incubator's
      // prompt says Hatch, never "Not enough tickets." for an egg that needs none.
      if (!skipConfirm(slot, line)) return;
      skipReadLeft = minutesLeftIn(slot);
      startSkipTick();
    });
  };
  const placeEgg = (slot: string, eggSpecies: string): void => {
    void ask(() => ship.place(eggSpecies, slot)).then((error) => {
      if (!disposed && error !== null) refusal(error);
    });
  };
  /**
   * Hatches a ready egg: the server's claim first, then the view's sequence (the walker held meanwhile), then the
   * hatchling stands at the hatch spot of its room (a guest) and the banner shows. The end is the sequence's own (the
   * view's event clock), never a timer here. A refusal or no answer says so; the egg stays ready to try again.
   */
  const hatch = (slot: string): void => {
    const job = jobIn(slot);
    if (job === null) return;
    void ask(() => ship.claim(job.id)).then((r) => {
      if (disposed) return;
      if (r.ok) playHatch(slot, r.monster);
      else refusal(r.error);
    });
  };
  const playHatch = (slot: string, monster: RosterMonster): void => {
    const from = scene.interactables.find((i) => i.id === slot);
    // The hatch spot of the incubator's room: the nearest one.
    let spot: SceneState['scene']['interactables'][number] | undefined;
    for (const i of scene.interactables) {
      if (i.kind !== 'hatch_spot') continue;
      if (spot === undefined || from === undefined || Math.hypot(i.x - from.x, i.y - from.y) < Math.hypot(spot.x - from.x, spot.y - from.y)) spot = i;
    }
    const where = scene.id;
    snap = { ...snap, hatching: true };
    held.keys = null;
    held.pad = null;
    applyHeld();
    cancelRoute(state);
    setRoute(null);
    refreshPrompt();
    notify();
    spritesOf.load(view, spriteSources.filter((s) => s.id === monster.species));
    const sequence = spot === undefined ? Promise.resolve() : view.playHatch(spot.id);
    void sequence.then(() => {
      if (disposed) return;
      if (spot !== undefined) ship.addGuest({ id: monster.id, species: monster.species, scene: where, spot: spot.id });
      snap = { ...snap, hatching: false };
      applyHeld();
      applyGuests();
      showBanner(bannerOf('ui.banner.hatched', { monster: monster.species }));
      refreshPrompt();
      wake();
    });
  };
  const clearTimer = (): void => {
    if (timer !== null) window.clearTimeout(timer);
    timer = null;
  };
  /** Asks for an animation frame unless one is asked for (or the page is hidden); the time slept passes first. */
  const wake = (): void => {
    if (frame !== null || document.hidden || disposed) return;
    clearTimer();
    if (sleptAt !== null) {
      rest(state, (performance.now() - sleptAt) / 1000);
      sleptAt = null;
    }
    frames += 1;
    frame = requestAnimationFrame(tick);
  };
  /** What happens next: a frame, a timer (a patroller's wait; a sprite's idle step at Off), or nothing until a wake. */
  const plan = (viewWants: number | null): void => {
    clearTimer();
    const input = held.keys !== null || held.pad !== null;
    const model = pending(state);
    if (input || model === 0 || viewWants === 0) {
      frames += 1;
      frame = requestAnimationFrame(tick);
      return;
    }
    last = null;
    sleptAt = performance.now();
    if (model !== null && (viewWants === null || model * 1000 <= viewWants)) {
      timer = window.setTimeout(wake, model * 1000);
    } else if (viewWants !== null) {
      // A sprite's idle loop at Off: a step of the view alone, no animation frame.
      timer = window.setTimeout(() => {
        timer = null;
        if (document.hidden) return;
        if (sleptAt !== null) {
          rest(state, (performance.now() - sleptAt) / 1000);
          sleptAt = performance.now();
        }
        plan(view.update(0, state));
      }, viewWants);
    }
  };
  const tick = (time: number): void => {
    frame = null;
    if (document.hidden) {
      last = null;
      sleptAt = performance.now();
      return;
    }
    const dt = last === null ? 0 : Math.min(time - last, uiTiming.sceneMaxStepMs) / 1000;
    last = time;
    if (!snap.fading) {
      const k = held.keys;
      const p = held.pad;
      const move = k === null && p === null ? null : { x: (k?.x ?? 0) + (p?.x ?? 0), y: (k?.y ?? 0) + (p?.y ?? 0) };
      const result = step(state, { move, run }, dt);
      if (snap.route !== null && state.player.route.length === 0) setRoute(null);
      if (result.portal !== null) cross(result.portal.to, result.portal.spawn);
      refreshPrompt();
    }
    plan(view.update(dt, state));
  };
  onChange = wake;
  refreshPrompt();
  mailBanner();
  wake();
  const onVisibility = (): void => {
    last = null;
    if (document.hidden) clearTimer();
    else wake();
  };
  document.addEventListener('visibilitychange', onVisibility);
  motion.addEventListener('change', applyMotion);
  return {
    get view() {
      return view;
    },
    snapshot: () => snap,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    state: () => state,
    hold,
    holdDir(dir) {
      hold('pad', dir === null ? null : dirOffset[dir]);
    },
    setRun(next) {
      run = next;
      wake();
    },
    tap(x, y) {
      if (snap.fading || snap.overlay !== null || paused || snap.flying !== null || snap.hatching) return false;
      const at = view.pickGround(x, y);
      if (at === null) {
        cancelRoute(state);
        setRoute(null);
        return false;
      }
      const ok = routeTo(state, at);
      setRoute(ok ? at : null);
      wake();
      return ok;
    },
    cancelRoute() {
      cancelRoute(state);
      setRoute(null);
    },
    travel(to, at) {
      if (snap.fading) return;
      cross(to, at);
    },
    frameRequests: () => frames,
    setLocale(next) {
      snap = { ...snap, locale: next, strings: tables[next] };
      notify();
    },
    setTier(next) {
      view.setTier(next);
      snap = { ...snap, tier: view.tier };
      notify();
      wake();
    },
    resize(width, height) {
      resizeCount += 1;
      view.resize(width, height);
      wake();
    },
    resizes: () => resizeCount,
    crossings: () => crossings,
    sprites: () => spritesOf.report(),
    place(x, y) {
      state.player.x = x;
      state.player.y = y;
      state.player.h = state.map.heightAt(x, y);
      state.revision += 1;
      cancelRoute(state);
      setRoute(null);
      view.snapCamera();
      refreshPrompt();
      wake();
    },
    face(facing) {
      state.player.facing = facing;
      state.revision += 1;
      wake();
    },
    action() {
      if (snap.fading || snap.overlay !== null || paused || asking || snap.flying !== null || snap.hatching) return false;
      const near = interactableNear(state);
      if (near === null) return false;
      switch (near.kind) {
        case 'talk':
          setOverlay({ kind: 'dialogue', speaker: `${near.target ?? ''}.name`, pages: [`${near.target ?? ''}.talk`], page: 0 });
          return true;
        case 'read': {
          const { title, pages } = readKeys(scene.id, near.id);
          if (pages.length === 0) return false;
          setOverlay({ kind: 'dialogue', speaker: title, pages, page: 0 });
          return true;
        }
        case 'wish':
          state.spent.push(near.id);
          setOverlay({ kind: 'dialogue', speaker: null, pages: ['ui.wish.made'], page: 0 });
          return true;
        case 'shop': {
          // The market of the scene's place; the keeper is the interactable's target (content lint: a resident here).
          const town = towns[scene.place];
          if (town === undefined || near.target === undefined) return false;
          setOverlay({ kind: 'shop', keeper: `${near.target}.name`, offers: marketOffers(town, pack.items) });
          return true;
        }
        case 'door':
        case 'board': {
          // A door enters; the town's mooring boards the airship (ADR 0033): both cross through the portal of their id.
          const portal = portalFor(scene, near);
          if (portal === null) return false;
          cross(portal.to, portal.spawn);
          return true;
        }
        case 'fly': {
          // The routes from the town the ship is moored at (the scene's place); no cost until roadmap step 6.
          const destinations = routes.filter((r) => r.from === scene.place).map((r) => r.to);
          if (destinations.length === 0 || handlers.onFly === undefined) return false;
          setOverlay({ kind: 'course', destinations });
          return true;
        }
        case 'incubator': {
          const job = jobIn(near.id);
          if (job !== null) {
            if (jobState(job, ship.time.now()) === 'ready') {
              hatch(near.id);
              return true;
            }
            incubatingEgg(near.id, job);
            return true;
          }
          const eggs = ship.state().eggs.map((e) => e.species);
          if (eggs.length === 0) return false;
          // One egg aboard goes in at once; with more, a menu asks which (a row per species aboard, with its count).
          const kinds = [...new Set(eggs)];
          const only = eggs[0];
          if (eggs.length === 1 && only !== undefined) placeEgg(near.id, only);
          else setOverlay({ kind: 'eggs', slot: near.id, eggs: kinds.map((s) => ({ species: s, count: eggs.filter((e) => e === s).length })) });
          return true;
        }
        case 'hatch_spot':
        case 'plot':
        case 'bench':
        case 'rack':
        case 'fixture':
          return false;
      }
    },
    advance() {
      const o = snap.overlay;
      if (o === null || o.kind !== 'dialogue') return;
      setOverlay(o.page + 1 < o.pages.length ? { ...o, page: o.page + 1 } : null);
    },
    close() {
      const o = snap.overlay;
      if (o !== null) setOverlay(null);
    },
    say(speaker, pages) {
      if (pages.length === 0) return;
      setOverlay({ kind: 'dialogue', speaker, pages, page: 0 });
    },
    shop(row) {
      const o = snap.overlay;
      // The goods are read-only rows (no wallet until roadmap step 6, ADR 0032): Leave is the one action.
      if (o === null || o.kind !== 'shop' || row !== 'leave') return;
      setOverlay(null);
    },
    course(row) {
      const o = snap.overlay;
      if (o === null || o.kind !== 'course') return;
      setOverlay(null);
      // A destination opens the packing panel for it (slice 8f, Q85); Go there flies.
      if (o.destinations.includes(row)) openPack(row);
    },
    pack(row) {
      const o = snap.overlay;
      if (o === null || o.kind !== 'pack') return;
      if (row.startsWith('store:')) {
        const next = moveIn(o.bag, o.storage, row.slice('store:'.length));
        if (next === 'full') setPack(o.bag, 'ui.pack.full');
        else setPack(next);
        return;
      }
      if (row.startsWith('bag:')) {
        // Only a stack at "×0" leaves: with a count, confirming the row (a tap, Enter) changes nothing.
        const item = row.slice('bag:'.length);
        if (o.bag.some((b) => b.item === item && b.count < 1)) setPack(confirmBagRow(o.bag, item));
        return;
      }
      switch (row) {
        case 'auto':
          setPack(auto(o.storage));
          return;
        case 'empty':
          setPack([]);
          return;
        case 'retry':
          readPack();
          return;
        case 'go':
          // While storage is read, Go waits for it (never a bag of the cached storage); stuck, it does nothing.
          if (o.stuck) return;
          if (o.reading) goWhenRead = true;
          else goPack();
          return;
        case 'cancel':
          goWhenRead = false;
          setOverlay(null);
          return;
        default:
          // A dimmed row (a heading, a line): nothing to do.
          return;
      }
    },
    packAdjust(row, step) {
      const o = snap.overlay;
      if (o === null || o.kind !== 'pack' || !row.startsWith('bag:')) return;
      setPack(adjust(o.bag, o.storage, row.slice('bag:'.length), step));
    },
    repack(destination, bag) {
      if (disposed || snap.flying !== null) return;
      openPack(destination, { bag, refusals: packRefusals + 1 });
    },
    egg(row) {
      const o = snap.overlay;
      if (o === null || o.kind !== 'eggs') return;
      setOverlay(null);
      if (o.eggs.some((e) => e.species === row)) placeEgg(o.slot, row);
    },
    incubating(row) {
      const o = snap.overlay;
      if (o === null || o.kind !== 'incubating') return;
      // A plan the wallet cannot cover is never sent (its row is dimmed; the server would refuse it anyway).
      if (row === 'skip' && !o.price.affordable) return;
      // Skip spends nothing yet (slice 8h, Q88): the confirmation opens, the account read again as it does.
      if (row === 'skip' && openSkip(o.slot, null)) return;
      setOverlay(null);
    },
    skipChoice(row) {
      const o = snap.overlay;
      if (o === null || o.kind !== 'skip') return;
      // While the account is read the rows wait: nothing is confirmed from a read that may be stale.
      if (o.reading && row !== 'cancel') return;
      if (row === 'retry') {
        readSkip(true);
        return;
      }
      const choice = o.failed === null ? o.choices.find((c) => c.key === row) : undefined;
      // A choice the wallet cannot pay is never sent (its row is dimmed; the server would refuse it anyway).
      if (choice !== undefined && !choice.plan.affordable) return;
      setOverlay(null);
      stopSkipTick();
      // The game's row is the game's choice at the server's time (`auto`); a row by hand, exactly its tickets.
      if (choice !== undefined) skipEgg(o.slot, o.job, choice.auto ? 'auto' : choice.plan.tickets);
    },
    banner(key, values) {
      showBanner({ ...bannerOf(key), values });
    },
    setLighting(next) {
      lightingAsked[timeKindOf(scene.kind)] = next;
      const shown = lightingFor(scene, next);
      view.setLighting(shown);
      if (shown === snap.lighting) return;
      snap = { ...snap, lighting: shown };
      placeCrew();
      notify();
      wake();
    },
    setPaused(next) {
      paused = next;
      setOverlay(snap.overlay);
    },
    refreshShip() {
      applyEggs();
      refreshPrompt();
      wake();
    },
    land() {
      if (disposed || snap.flying === null) return;
      snap = { ...snap, flying: null };
      applyHeld();
      view.setFlight(false);
      placeCrew();
      notify();
      refreshPrompt();
      wake();
    },
    dispose() {
      disposed = true;
      if (frame !== null) cancelAnimationFrame(frame);
      frame = null;
      clearTimer();
      if (readyTimer !== null) window.clearTimeout(readyTimer);
      stopSkipTick();
      unsubscribeShip();
      document.removeEventListener('visibilitychange', onVisibility);
      motion.removeEventListener('change', applyMotion);
      listeners.clear();
      return view.dispose();
    },
    remount(next) {
      if (frame !== null) cancelAnimationFrame(frame);
      frame = null;
      clearTimer();
      const left = view.dispose();
      view = mount(next);
      applyMotion();
      spritesOf.load(view, spriteSources.filter((s) => species().includes(s.id)));
      show(view);
      applyEggs();
      applyGuests();
      snap = { ...snap, tier: view.tier };
      notify();
      last = null;
      wake();
      return left;
    },
  };
};
